<template>
  <div
      class="modal fade coupon-gift-modal"
      :id="modalId"
      tabindex="-1"
      role="dialog"
      aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content coupon-gift-modal__content" v-if="item">
        <button
            type="button"
            class="close coupon-gift-modal__close"
            data-dismiss="modal"
            aria-label="Schließen"
        >&times;</button>

        <div class="modal-body text-center">
          <span class="coupon-gift-modal__badge">Gratis-Geschenk</span>

          <div class="coupon-gift-modal__image" v-if="imageUrl">
            <img :src="imageUrl" :alt="itemName" loading="lazy">
          </div>

          <h4 class="coupon-gift-modal__title">{{ itemName }}</h4>

          <p class="coupon-gift-modal__text" v-html="bodyText"></p>

          <button
              class="btn btn-primary coupon-gift-modal__cta"
              :disabled="isLoading || isInBasket"
              @click="addToBasket"
          >
            <span v-if="isInBasket">Bereits im Warenkorb&nbsp;&#10003;</span>
            <span v-else-if="isLoading">Wird hinzugefügt&nbsp;…</span>
            <span v-else>Gratis in den Warenkorb</span>
          </button>

          <button
              type="button"
              class="btn btn-link coupon-gift-modal__decline"
              data-dismiss="modal"
          >Nein, danke</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '../services/ApiService'
import ExceptionMap from '../exceptions/ExceptionMap'

export default {
  name: 'coupon-gift-modal',

  props: {
    // Varianten-ID des Gratis-Artikels (Pflicht)
    variationId: {
      type: [Number, String],
      required: true,
    },
    // Artikel-ID (optional, aktuell nur informativ)
    itemId: {
      type: [Number, String],
      default: 0,
    },
    // Auslöser: 'coupon' = nach eingelöstem Gutscheincode, 'value' = nach Warenwert.
    trigger: {
      type: String,
      default: 'coupon',
    },
    // Gutscheincode, der das Popup auslöst (nur bei trigger = 'coupon').
    couponCode: {
      type: String,
      default: 'EXTRA5',
    },
    // Warenwert-Schwelle, ab der das Popup erscheint (nur bei trigger = 'value').
    minBasketValue: {
      type: [Number, String],
      default: 0,
    },
    headline: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      item: null,
      isLoading: false,
    }
  },

  computed: {
    modalId() {
      return 'coupon-gift-modal-' + this.variationId
    },
    // Merkt sich pro Session, für WELCHE Coupon-Anwendung das Popup schon gezeigt wurde.
    // Wird beim Entfernen/Ändern des Coupons zurückgesetzt -> erneutes Einlösen zeigt wieder.
    shownKey() {
      return 'kuipers-coupon-gift-shown-' + this.variationId
    },
    // Dauerhaftes Flag: einmal aktiv geschlossen (X / "Nein danke" / ESC / Backdrop)
    // -> Popup öffnet sich nicht mehr wieder, auch nicht bei erneutem Einlösen.
    dismissedKey() {
      return 'kuipers-coupon-gift-dismissed-' + this.variationId
    },
    targetCoupon() {
      return String(this.couponCode).toUpperCase()
    },
    minValue() {
      return Number(this.minBasketValue) || 0
    },
    basket() {
      return this.$store.state.basket && this.$store.state.basket.data
    },
    basketItems() {
      return (this.$store.state.basket && this.$store.state.basket.items) || []
    },
    appliedCoupon() {
      return this.basket && this.basket.couponCode ? String(this.basket.couponCode).toUpperCase() : ''
    },
    isInBasket() {
      return this.basketItems.some(
          (basketItem) => Number(basketItem.variationId) === Number(this.variationId),
      )
    },
    // Warenkorb-Position des Gratis-Artikels (für Warenwert OHNE Geschenk).
    giftItem() {
      return this.basketItems.find(
          (basketItem) => Number(basketItem.variationId) === Number(this.variationId),
      )
    },
    // Warenwert ohne das Geschenk selbst (analog GiftManager), damit die Schwelle
    // sauber ausgewertet wird.
    basketValueWithoutGift() {
      let total = this.basket && this.basket.itemSum != null ? Number(this.basket.itemSum) : NaN
      if (isNaN(total)) {
        total = this.basketItems.reduce(
            (sum, bi) => sum + (Number(bi.price) || 0) * bi.quantity, 0,
        )
      }
      const gift = this.giftItem
      const giftValue = gift ? (Number(gift.price) || 0) * gift.quantity : 0
      return total - giftValue
    },
    // Ist die konfigurierte Bedingung erfüllt? Je nach Auslöser Warenwert ODER Coupon.
    conditionMet() {
      if (this.trigger === 'value') {
        return this.minValue > 0 && this.basketValueWithoutGift >= this.minValue
      }
      return !!this.targetCoupon && this.appliedCoupon === this.targetCoupon
    },
    // Kennung der aktuellen "Auslösung" für die Einmal-pro-Anwendung-Merkung.
    conditionKey() {
      return this.trigger === 'value' ? 'value' : this.targetCoupon
    },
    itemName() {
      if (!this.item) {
        return ''
      }
      const nameFilter = this.$options.filters && this.$options.filters.itemName
      if (nameFilter) {
        return nameFilter(this.item)
      }
      return (this.item.texts && this.item.texts.name1) || ''
    },
    imageUrl() {
      if (!this.item || !this.item.images) {
        return ''
      }
      const images =
          this.item.images.variation && this.item.images.variation.length
              ? this.item.images.variation
              : this.item.images.all
      const image = images && images[0]
      return image ? image.urlMiddle || image.urlPreview || image.url : ''
    },
    bodyText() {
      const fallback =
          'Als Dankeschön für Ihren Gutschein <b>' +
          this.couponCode +
          '</b> schenken wir Ihnen <b>{name}</b> gratis dazu.'
      return (this.text || fallback).replace(/\{name\}/g, this.itemName)
    },
  },

  mounted() {
    // Jede Art des Schließens (X, "Nein danke", ESC, Klick auf Backdrop) merken,
    // damit sich das Popup danach nicht erneut öffnet.
    $('#' + this.modalId).on('hidden.bs.modal', () => {
      localStorage.setItem(this.dismissedKey, '1')
    })
  },

  watch: {
    // Reagiert auf beide Auslöser: conditionMet kapselt Coupon ODER Warenwert.
    conditionMet: {
      immediate: true,
      handler() {
        this.evaluateTrigger()
      },
    },
  },

  methods: {
    /**
     * Reagiert auf Änderungen der Auslöse-Bedingung (Coupon eingelöst ODER
     * Warenwert erreicht):
     * - Bedingung frisch erfüllt   -> Popup einmal pro Anwendung zeigen
     * - Bedingung nicht (mehr) erfüllt -> Merkung zurücksetzen, damit erneutes
     *   Erfüllen das Popup wieder auslöst (sofern nicht aktiv geschlossen)
     */
    evaluateTrigger() {
      // Warenkorb noch nicht geladen -> abwarten (kein Fehl-Reset).
      if (!this.basket) {
        return
      }

      if (!this.conditionMet) {
        sessionStorage.removeItem(this.shownKey)
        return
      }

      // Nutzer hat das Popup aktiv geschlossen -> nie wieder anzeigen.
      if (localStorage.getItem(this.dismissedKey)) {
        return
      }

      // Diese Auslösung wurde bereits behandelt (z. B. Seitenreload).
      if (sessionStorage.getItem(this.shownKey) === this.conditionKey) {
        return
      }
      // Als behandelt markieren -> kein erneutes Aufpoppen bei Reloads.
      sessionStorage.setItem(this.shownKey, this.conditionKey)

      // Gratis-Artikel schon im Warenkorb -> kein Popup nötig.
      if (this.isInBasket) {
        return
      }

      this.fetchItem().then(() => {
        if (!this.item || this.isInBasket) {
          return
        }
        this.$nextTick(() => {
          $('#' + this.modalId).modal('show')
        })
      })
    },

    /**
     * Lädt Name/Bild/Preis allein anhand der Varianten-ID nach.
     */
    fetchItem() {
      if (this.item) {
        return Promise.resolve()
      }
      return new Promise((resolve) => {
        ApiService.get('/rest/io/variations/' + this.variationId, null, {
          supressNotifications: true,
        })
            .done((response) => {
              if (response && response.documents && response.documents[0]) {
                this.item = response.documents[0].data
              }
              resolve()
            })
            .fail(() => resolve())
      })
    },

    /**
     * Legt den Gratis-Artikel mit einem Klick in den Warenkorb.
     * Der Preis (0 €) wird über die Plenty-Aktionsregel geregelt.
     */
    addToBasket() {
      if (this.isInBasket || this.isLoading) {
        return
      }
      this.isLoading = true

      const basketObject = {
        variationId: Number(this.variationId),
        quantity: 1,
        basketItemOrderParams: [],
        totalOrderParamsMarkup: 0,
      }

      this.$store.dispatch('addBasketItem', basketObject).then(
          () => {
            document.dispatchEvent(
                new CustomEvent('afterBasketItemAdded', { detail: basketObject }),
            )
            this.isLoading = false
            this.$nextTick(() => {
              $('#' + this.modalId).modal('hide')
            })
          },
          (error) => {
            this.isLoading = false
            if (error && error.data) {
              window.CeresNotificationService.error(
                  this.$translate(
                      'Ceres::Template.' + ExceptionMap.get(error.data.exceptionCode.toString()),
                      error.data.placeholder,
                  ),
              ).closeAfter(5000)
            }
          },
      )
    },
  },
}
</script>